import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

/**
 * Sets the browser tab title from each route's `title`, treating that value as a
 * translation key. Angular's default strategy writes the raw string verbatim, so
 * key-based titles showed literally (e.g. "agentTasks.pendingTitle") and plain
 * English titles never translated.
 *
 * Resolution: translate the key; if no translation exists (instant() returns the
 * key unchanged) fall back to the raw string so untranslated titles still render.
 * The active title is re-applied on language change so the tab updates live.
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
  private currentTitle: string | undefined;

  constructor(
    private readonly title: Title,
    private readonly translate: TranslateService
  ) {
    super();
    this.translate.onLangChange.subscribe(() => this.apply(this.currentTitle));
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.currentTitle = this.buildTitle(snapshot);
    this.apply(this.currentTitle);
  }

  private apply(titleOrKey: string | undefined): void {
    if (!titleOrKey) {
      return;
    }
    const translated = this.translate.instant(titleOrKey);
    this.title.setTitle(
      translated && translated !== titleOrKey ? translated : titleOrKey
    );
  }
}
